// Historia 179 — lo que la ficha decide sobre el workflow de la OF, sin pintar nada.
import { WITHOUT_WORKFLOW_OPTION_ID, sortedWorkflowOptions, workflowOptionsWithSentinel } from '../withoutWorkflowOption'

/**
 * El nombre con el que se ESCRIBE un workflow en la ficha: el mismo que usan las opciones de C10
 * (`Workflow.getVersionedName` en Sun: el código y, si la versión no es 1, " - V<n>"), para que el valor
 * del campo y sus opciones se lean igual.
 */
export const workflowLabelOf = (workflow) => {
  const version = Number(workflow.version)
  if (workflow.version === '' || !Number.isFinite(version) || version === 1) return workflow.code
  return `${workflow.code} - V${Math.round(version * 10) / 10}`
}

// Mapa motivo → clave (03-dominio §i18n «Mapa motivo → clave»): los cinco de RN18.1-RN18.5 y el de clúster
// (EM-2). Es el MISMO para el tooltip de C1 y para las líneas de E1: un motivo se escribe igual en los dos.
const REASON_KEYS = {
  STATUS_NOT_PENDING: 'The workflow can only be changed while the fabrication order is PENDING',
  PARTS_NOT_RAW: 'The workflow cannot be changed because some parts have already started',
  ACTIVE_JOB: 'The workflow cannot be changed while a job is active',
  HAS_CHILDREN: 'The workflow cannot be changed because the fabrication order has child fabrication orders',
  PLANNING_IN_PROGRESS: 'The workflow cannot be changed while a planning is being calculated',
  REPLICATED_IN_CLUSTER: 'The workflow cannot be removed because the fabrication order is replicated in another cluster node'
}

/**
 * La clave de un motivo. Un motivo que este cliente no conoce se enseña tal cual (su código): callarlo
 * haría creer que no hay motivo, y es justo lo que el tooltip existe para decir.
 */
export const reasonKeyOf = (reason) => REASON_KEYS[reason] || reason

/** ¿La opción elegida es "Without workflow"? Por identidad, nunca por texto (RN9). */
export const isRemoval = (option) => option.id === WITHOUT_WORKFLOW_OPTION_ID

/**
 * La operación PREVISTA al elegir una opción (RN17): nulo→WF asignar, WF→WF' cambiar, WF→nulo quitar. Es
 * lo que decide el diálogo; lo que de verdad pasó lo dice Sun en la respuesta (`kind`), que puede ser
 * `NO_OP`.
 */
export const plannedKindOf = (assignment, option) => {
  if (assignment.withoutWorkflow) return 'ASSIGN'
  return isRemoval(option) ? 'CLEAR' : 'CHANGE'
}

// G12: título y texto del diálogo por operación prevista (03-dominio §i18n).
export const CONFIRMATION_TEXTS = {
  ASSIGN: {
    title: 'Assign workflow',
    body: 'Assigning the workflow makes this fabrication order plannable and triggers a replanning. Continue?'
  },
  CHANGE: {
    title: 'Change workflow',
    body: "Changing the workflow replaces this fabrication order's workflow copy with a new copy of the selected workflow. Changes made to the current copy will no longer apply, and a replanning is triggered. Continue?"
  },
  CLEAR: {
    title: 'Remove workflow',
    body: 'Removing the workflow leaves this fabrication order without workflow: it will not be planned until a workflow is assigned. Continue?'
  }
}

/**
 * Lo que la ficha recarga tras un C2 correcto (CA8, CA10): la cabecera y General (con ellas, la marca
 * `lastModified` nueva) y los bloques que se derivan de la ruta —Operaciones, Documentos, Materiales y
 * Progreso—. El de las HIJAS solo si C2 dice que se generaron (`generatedChildCount > 0`, en ASSIGN o
 * CHANGE): las hijas no se tocan en ningún otro caso (RN23).
 */
export const detailBlocksToReload = (result) => [
  'header', 'sheet', 'Operations', 'Documents', 'Materials', 'Progress',
  ...(result.generatedChildCount > 0 ? ['Children'] : [])
]

// Mapa `code` → clave (B3, 03-dominio §i18n). Jupiter decide por el `code`, NUNCA por el `message`: el de
// E7 ni siquiera es fijo (lleva el estado, `contrato-sun.md` §4).
const REJECTION_KEYS = {
  E_FO_WORKFLOW_NOT_EDITABLE: 'The workflow of this fabrication order cannot be changed',
  E_FO_WORKFLOW_NOT_ADMISSIBLE: 'The workflow is not an approved workflow of the fabrication order part reference',
  E_FO_WORKFLOW_NOT_FOUND: 'Workflow not found',
  E_FO_MODIFIED_BY_ANOTHER_USER: 'Fabrication order was modified by another user',
  E_FO_NOT_FOUND: 'Fabrication order not found',
  E_FO_CHILD_WORKFLOW_NOT_DEFINED: "Child part reference '{{name}}' workflow is not defined",
  E_STATUS_NOT_ALLOWED_EDIT: 'You are not allowed to edit this fabrication order',
  E_FO_EXPECTED_LAST_MODIFIED_INVALID: 'The workflow change could not be saved. Reload the fabrication order and try again',
  // EM-1: E12 no debería llegar nunca (Jupiter manda siempre `workflowId`); si llega, con la clave de E9.
  E_FO_WORKFLOW_ID_REQUIRED: 'The workflow change could not be saved. Reload the fabrication order and try again'
}

const FORBIDDEN_KEY = REJECTION_KEYS.E_STATUS_NOT_ALLOWED_EDIT
// Lo que no es contrato (500 `E_INTERNAL_ERROR`, F3; un `code` desconocido; un fallo de red): error
// genérico. Es la clave que General ya usa para un guardado que no se pudo hacer.
const GENERIC_KEY = 'The changes could not be saved'

/**
 * Cómo se enseña un rechazo de C1/C2 (B3, S111, S121): la clave del aviso (y sus parámetros), las líneas
 * de motivos (E1), y si tras él hay que releer C1 (cualquier 409). `concurrent` es E4, que además conserva
 * la selección mientras el campo siga siendo editable (S110).
 *
 * ⚠️ CUALQUIER 403 es E7, con o sin `code`: el de autoridades lo escribe Spring Security y no trae `code`
 * (VC-18, `contrato-sun.md` §7). Por eso el status se mira ANTES que el `code`.
 */
export const rejectionOf = (error) => {
  const status = error && error.status
  const data = (error && error.data) || {}
  if (status === 403) return { key: FORBIDDEN_KEY, params: {}, reasons: [], reloadsAssignment: false, concurrent: false }
  const key = REJECTION_KEYS[data.code] || GENERIC_KEY
  return {
    key,
    params: data.code === 'E_FO_CHILD_WORKFLOW_NOT_DEFINED' ? { name: data.partReferenceName || '' } : {},
    reasons: data.code === 'E_FO_WORKFLOW_NOT_EDITABLE' && Array.isArray(data.reasons) ? data.reasons : [],
    reloadsAssignment: status === 409,
    concurrent: data.code === 'E_FO_MODIFIED_BY_ANOTHER_USER'
  }
}

/** La opción "Without workflow" de la ficha: la misma centinela que el alta, por identidad. */
export const withoutWorkflowOptionOf = (t_) => ({ id: WITHOUT_WORKFLOW_OPTION_ID, name: t_('Without workflow') })

/**
 * Las opciones del campo (CU8): las de C10 —maestros admisibles, nunca copias— y "Without workflow" SOLO
 * si la OF se puede vaciar (`clearable`) o ya no tiene workflow (entonces es su valor actual). La
 * centinela es la misma del alta, con la misma desambiguación de nombre (R11).
 */
export const workflowFieldOptionsOf = (assignment, approvedWorkflows, t_) => ((assignment.clearable || assignment.withoutWorkflow)
  ? workflowOptionsWithSentinel(approvedWorkflows, t_)
  : sortedWorkflowOptions(approvedWorkflows))

/**
 * El valor actual del campo, como opción: la centinela si la OF no tiene workflow, si no el suyo. La
 * centinela sale de `options` para llevar la MISMA etiqueta que la ofrecida (desambiguada si hace falta).
 */
export const currentOptionOf = (assignment, options, t_) => (assignment.withoutWorkflow
  ? (options.find((option) => option.id === WITHOUT_WORKFLOW_OPTION_ID) || withoutWorkflowOptionOf(t_))
  // H187 (RN1, S59): the value is the copy CODE, without version suffix; the origin goes in the field helper text.
  : { id: assignment.workflow.id, name: assignment.workflow.code })
